<template>
  <div class="widget-body" v-resize:debounce.50="onResize">
    <v-alert show v-if="isNoData" variant="light" align="center">{{ $t('white-label.globalMessages.nodata') }}</v-alert>
    <div v-show="!isError" class="grid-parent">
      <c-loader v-if="isLoading" />
      <c-streaming-error :show="hasStreamingError" />
      <ag-grid-vue
        :class="['c-data-grid', 'ag-theme-balham-dark', view]"
        :style="marketDepthRowColors"
        :context="this"
        :defaultColDef="defaultColDef"
        :defaultColGroupDef="defaultGroupColDef"
        :gridOptions="gridOptions"
        immutableData
        :asyncTransactionWaitMillis="gridUpdateInterval"
        :rowClassRules="rowClassRules"
        :rowHeight="agGridRowHeight"
        :suppressColumnVirtualisation="isAccessibilityEnabled"
        suppressContextMenu
        suppressFieldDotNotation
        suppressMovableColumns
        suppressRowHoverHighlight
        v-show="!isNoData && !isError"
        @grid-ready="onTableReady"
        @cell-key-down="onCellKeyDown"
        @cell-focused="onCellFocused"
        @async-transactions-flushed="onAsyncTransactionsFlushed"
      />
    </div>
    <div class="h-100 d-flex align-items-center justify-content-center" v-if="isError">
      {{ $i18n('error') }}
    </div>
  </div>
</template>

<script>
import { AgGridVue } from 'ag-grid-vue';
import moment from 'moment';
import resize from 'vue-resize-directive';

import { API } from '@/state/api';
import { toTimezoneDate } from '@/utils/date';
import CLoader from '@custom/CLoader';
import CStreamingError from '@custom/CStreamingError';
import PriceQuoteRenderer from './priceQuoteRenderer.vue';
import styleVars from '@mm5-components/scss/variables.scss';
import VolumeRenderer from './volumeRenderer.vue';
import widgetCommons from '@/mixins/widgetCommons.js';
import gridNavigation from '@/mixins/gridNavigation.js';
import agGridCommons from '@mixins/agGridCommons';
import { contrast, hexToRGB } from '@/utils/colors';

export default {
  name: 'w-market-depth-l2-widget',
  mixins: [widgetCommons, gridNavigation, agGridCommons],
  directives: { resize },
  components: {
    CLoader,
    AgGridVue,
    VolumeRenderer,
    PriceQuoteRenderer,
    CStreamingError
  },
  props: {
    wid: {
      type: String,
      required: true
    },
    ric: {
      type: String,
      required: true
    },
    view: {
      type: String,
      required: true
    },
    buyAction: {
      type: Function
    },
    sellAction: {
      type: Function
    },
    embedded: {
      type: Boolean
    }
  },
  data() {
    return {
      defaultColDef: {
        headerClass: 'text-left',
        resizable: false,
        sortable: false,
        suppressMenu: true
      },
      defaultGroupColDef: {
        headerClass: 'text-center',
        marryChildren: true
      },
      isLoading: false,
      isNoData: false,
      isError: false,
      hasStreamingError: false,
      gridOptions: {
        getRowNodeId: rowData => rowData._index,
        columnTypes: { actionable: {} },
        tabToNextHeader: this.tabToNextHeader,
        tabToNextCell: this.tabToNextCell
      },
      throttledSetRowData: null,
      cachedData: {},
      subscriptionId: null,
      responseTimeout: 5000, //Timeout in case of no response from MD streaming (in milliseconds)
      pendingTimeout: null,
      dateFormatter: row => {
        return row.value ? moment(toTimezoneDate(this.timezone, row.value)).format('DD MMM YY HH:mm') : '';
      },
      maxAskSize: 0,
      maxBidSize: 0,
      rowClassRules: {
        'ask-0': 'data._cellVariants.ask === 0',
        'ask-1': 'data._cellVariants.ask === 1',
        'ask-2': 'data._cellVariants.ask === 2',
        'ask-3': 'data._cellVariants.ask === 3',
        'ask-4': 'data._cellVariants.ask === 4',
        'bid-0': 'data._cellVariants.bid === 0',
        'bid-1': 'data._cellVariants.bid === 1',
        'bid-2': 'data._cellVariants.bid === 2',
        'bid-3': 'data._cellVariants.bid === 3',
        'bid-4': 'data._cellVariants.bid === 4'
      },
      defaultRowColors: {
        0: 'var(--primary-lighten-2)',
        1: 'var(--primary-lighten-1)',
        2: 'var(--primary)',
        3: 'var(--primary-darken-1)',
        4: 'var(--primary-darken-2)'
      },
      defaultTextColors: {
        0: 'var(--primary-lighten-2-fg)',
        1: 'var(--primary-lighten-1-fg)',
        2: 'var(--primary-fg)',
        3: 'var(--primary-darken-1-fg)',
        4: 'var(--primary-darken-2-fg)'
      }
    };
  },
  computed: {
    agGridRowHeight() {
      return parseInt(styleVars['rowHeight' + this.fontSize.toUpperCase()]);
    },
    fontScale() {
      return {
        xs: 0.85,
        s: 0.9,
        m: 1,
        l: 1.25,
        xl: 1.5
      }[this.fontSize];
    },
    mdRic() {
      if (!this.ric.includes('.') || /\.(A|K|N|O|OQ|P)$/.test(this.ric)) {
        const ricBase = this.ric.split('.')[0];
        return `1_MD_${ricBase}.O`;
      }
      return '1_MD_' + this.ric;
    },
    customRowColors() {
      const colors = this.$store.state.generalConfig.marketDepthColors || {};
      return Object.keys(this.defaultRowColors).map(index => {
        return colors[index] || this.defaultRowColors[index];
      });
    },
    customTextColors() {
      return Object.keys(this.defaultTextColors).map(index => {
        return this.defaultTextColors[index];
      });
    },
    marketDepthRowColors() {
      let mapOfRowColors = this.customRowColors.reduce((map, item, index) => {
        map[`--color-${index}`] = item;
        return map;
      }, {});

      let mapOfRowTextColors = this.customTextColors.reduce((map, item, index) => {
        map[`--color-${index}-text`] = item;
        return map;
      }, {});

      Object.keys(mapOfRowColors).forEach(key => {
        mapOfRowTextColors[key + '-text'] = this.getContrastTextColor(mapOfRowColors[key])
          ? this.getContrastTextColor(mapOfRowColors[key])
          : mapOfRowTextColors[key + '-text'];
      });

      mapOfRowColors = Object.assign(mapOfRowColors, mapOfRowTextColors);
      return mapOfRowColors;
    },
    locale() {
      return this.$i18n.locale;
    },
    timezone() {
      return this.$store.state.applicationSettings.general.settings.timezone;
    }
  },
  watch: {
    fontSize() {
      this.$nextTick(() => {
        this.setRowsHeight();
        this.updateColumnDefs(true);
      });
    },
    ric: {
      handler(newVal, oldVal) {
        if (newVal !== oldVal) {
          this.streamingUnsubscribe();
        }
        this.fetch();
      }
    },
    view() {
      // clearing table as workaround for scrolling to top
      if (Object.keys(this.cachedData).length) {
        this.gridOptions.api.setRowData([]);
        this.updateColumnDefs();
        this.gridOptions.api.setRowData(this.calcMarketDepthData());
      }
    },
    locale() {
      moment.locale(this.locale);
      this.updateColumnDefs();
      if (this.gridOptions && this.gridOptions.api)
        this.gridOptions.api.refreshCells({
          columns: ['bid_timestamp', 'bid_size', 'ask_size', 'bid', 'ask', 'ask_timestamp'],
          force: true
        });
    },
    timezone() {
      if (this.gridOptions && this.gridOptions.api)
        this.gridOptions.api.refreshCells({
          columns: ['bid_timestamp', 'ask_timestamp'],
          force: true
        });
    }
  },
  methods: {
    createFieldOptions() {
      return {
        bid_broker: {
          field: 'bid_broker',
          flex: 1,
          minWidth: Math.round(50 * this.fontScale),
          headerName: this.i18n('header.broker'),
          headerClass: 'text-right',
          cellClass: 'text-right',
          tooltipField: 'bid_broker'
        },
        bid_timestamp: {
          field: 'bid_timestamp',
          headerName: this.i18n('header.timestamp'),
          headerClass: 'text-center',
          valueFormatter: this.dateFormatter,
          cellClass: 'text-center',
          minWidth: Math.round(102 * this.fontScale),
          width: Math.round(102 * this.fontScale)
        },
        bid_size: {
          field: 'bid_size',
          headerClass: 'text-right',
          headerName: this.i18n('header.bidSize'),
          cellClass: ['text-right', 'p-0'],
          cellRenderer: 'VolumeRenderer',
          cellRendererParams: () => {
            return { direction: 'rightJustified', valueType: 'bid', marketDepthRowColors: this.marketDepthRowColors };
          },
          minWidth: Math.round(54 * this.fontScale),
          width: Math.round(54 * this.fontScale)
        },
        num_bid: {
          field: 'num_bid',
          headerClass: 'text-right',
          headerName: this.i18n('header.numBid'),
          cellClass: 'text-right',
          minWidth: Math.round(35 * this.fontScale),
          width: Math.round(35 * this.fontScale)
        },
        bid: {
          field: 'bid',
          headerClass: 'text-right',
          headerName: this.i18n('header.price'),
          cellClass: 'full-space-cell',
          cellRenderer: 'PriceQuoteRenderer',
          cellRendererParams: () => ({
            ric: this.ric,
            buyAction: this.buyAction,
            sellAction: this.sellAction,
            embedded: this.embedded
          }),
          valueFormatter: row => this.$f(row.value, 'float.price'),
          tooltipValueGetter: this.tooltipValueGetter('q._BID'),
          minWidth: Math.round(62 * this.fontScale),
          width: Math.round(62 * this.fontScale),
          type: ['actionable']
        },
        ask: {
          field: 'ask',
          headerName: this.i18n('header.price'),
          cellClass: 'full-space-cell',
          cellRenderer: 'PriceQuoteRenderer',
          cellRendererParams: () => ({
            ric: this.ric,
            buyAction: this.buyAction,
            sellAction: this.sellAction,
            embedded: this.embedded
          }),
          valueFormatter: row => this.$f(row.value, 'float.price'),
          tooltipValueGetter: this.tooltipValueGetter('q._ASK'),
          minWidth: Math.round(62 * this.fontScale),
          width: Math.round(62 * this.fontScale),
          type: ['actionable']
        },
        num_ask: {
          field: 'num_ask',
          headerName: this.i18n('header.numAsk'),
          cellClass: 'text-left',
          minWidth: Math.round(35 * this.fontScale),
          width: Math.round(35 * this.fontScale)
        },
        ask_size: {
          field: 'ask_size',
          headerName: this.i18n('header.askSize'),
          cellClass: ['text-left', 'p-0'],
          cellRenderer: 'VolumeRenderer',
          cellRendererParams: () => {
            return {
              direction: 'leftJustified',
              valueType: 'ask',
              marketDepthRowColors: this.marketDepthRowColors
            };
          },
          minWidth: Math.round(54 * this.fontScale),
          width: Math.round(54 * this.fontScale)
        },
        ask_timestamp: {
          field: 'ask_timestamp',
          headerName: this.i18n('header.timestamp'),
          headerClass: 'text-center',
          valueFormatter: this.dateFormatter,
          cellClass: 'text-center',
          minWidth: Math.round(102 * this.fontScale),
          width: Math.round(102 * this.fontScale)
        },
        ask_broker: {
          field: 'ask_broker',
          flex: 1,
          minWidth: Math.round(50 * this.fontScale),
          headerName: this.i18n('header.broker'),
          cellClass: 'text-left',
          tooltipField: 'ask_broker'
        }
      };
    },
    createColumnDefs() {
      const fieldOptions = this.createFieldOptions();
      const bidCols =
        this.view === 'by-price'
          ? [
              fieldOptions.bid_broker,
              fieldOptions.bid_timestamp,
              fieldOptions.bid_size,
              fieldOptions.num_bid,
              fieldOptions.bid
            ]
          : [fieldOptions.bid_broker, fieldOptions.bid_timestamp, fieldOptions.bid_size, fieldOptions.bid];
      const askCols =
        this.view === 'by-price'
          ? [
              fieldOptions.ask,
              fieldOptions.num_ask,
              fieldOptions.ask_size,
              fieldOptions.ask_timestamp,
              fieldOptions.ask_broker
            ]
          : [fieldOptions.ask, fieldOptions.ask_size, fieldOptions.ask_timestamp, fieldOptions.ask_broker];
      return [
        {
          headerName: this.$t('white-label.globalLabels.fids.q|_BID.full'),
          children: bidCols
        },
        {
          headerName: this.$t('white-label.globalLabels.fids.q|_ASK.full'),
          children: askCols
        }
      ];
    },
    onTableReady() {
      this.updateColumnDefs();
      this.gridOptions.api.setHeaderHeight(this.agGridRowHeight);
      this.gridOptions.api.resetRowHeights();

      this.fetch();
      // throttle updates because when trading is going on there can be several updates every second
      this.throttledSetRowData = this._.throttle(
        () => this.gridOptions.api.setRowData(this.calcMarketDepthData()),
        1000
      );
      this.gridOptions.api.setGridAriaProperty('label', this.i18n('widget-title'));
    },
    onResize() {
      if (!this.isNoData && !this.isError && !this.isLoading) {
        this.gridOptions.api.sizeColumnsToFit();
      }
    },
    calcMarketDepthData() {
      if (!Object.keys(this.cachedData).length) return [];
      const orders = this.processData(Object.values(this.cachedData));
      return this.view === 'by-price' ? this.groupDataByPrice(orders) : this.groupDataByOrder(orders);
    },
    updateColumnDefs(reset) {
      // there's an ag-grid bug, preventing it detecting the minWidth change in colDefs, so colDefs need to be reset first
      if (reset) this.gridOptions.api.setColumnDefs([]);
      this.gridOptions.api.setColumnDefs(this.createColumnDefs());
    },
    processData(data) {
      const asks = [];
      const bids = [];

      data.forEach(d => {
        ['BID', 'ASK'].forEach(type => {
          if (!d[type]) return;
          const date = d[type + 'DATE'] ? d[type + 'DATE'] : moment().format('DD MMM YYYY');
          const time = d[type + 'TIME'];
          const timestamp = time ? moment.utc(date + ' ' + time, 'DD MMM YYYY HH:mm:ss').valueOf() : 0;
          (type === 'ASK' ? asks : bids).push({
            [type.toLowerCase()]: +d[type],
            [type.toLowerCase() + '_size']: +d[type + 'SIZE'],
            [type.toLowerCase() + '_timestamp']: timestamp,
            [type.toLowerCase() + '_broker']: d[`NO_${type}MMKR`]
          });
        });
      });

      asks.sort((a, b) => a.ask - b.ask || a.ask_timestamp - b.ask_timestamp);
      bids.sort((a, b) => b.bid - a.bid || a.bid_timestamp - b.bid_timestamp);
      return {
        asks,
        bids
      };
    },
    groupDataByPrice(orders) {
      const dataRows = {};
      ['bid', 'ask'].forEach(type => {
        const groupedData = this._.groupBy(orders[type + 's'], type);
        dataRows[type] = Object.entries(groupedData).map(([price, values]) => {
          const res = {
            [type]: price,
            [type + '_size']: 0,
            ['num_' + type]: values.length,
            [type + '_broker']: new Set(),
            [type + '_timestamp']: 0
          };
          values.forEach(d => {
            res[type + '_size'] += d[type + '_size'];
            res[type + '_broker'].add(d[type + '_broker']);
            if (d[type + '_timestamp'])
              res[type + '_timestamp'] = res[type + '_timestamp']
                ? Math.min(res[type + '_timestamp'], d[type + '_timestamp'])
                : d[type + '_timestamp'];
          });
          res[type + '_broker'] = [...res[type + '_broker']].join(' ');
          return res;
        });
        dataRows[type].sort((a, b) => (a[type] - b[type]) * (type === 'bid' ? -1 : 1));
        const maxRow = this._.maxBy(dataRows[type], type + '_size');
        if (maxRow) this[`max${this._.capitalize(type)}Size`] = maxRow[type + '_size'];
      });
      const result = [];
      const len = Math.max(dataRows.ask.length, dataRows.bid.length);
      for (let i = 0; i < len; i++) {
        result[i] = {
          _index: i,
          _cellVariants: {},
          ...dataRows.ask[i],
          ...dataRows.bid[i]
        };
      }
      return result;
    },
    groupDataByOrder(orders) {
      const len = Math.max(orders.asks.length, orders.bids.length);
      const topPrices = { bid: [], ask: [] };
      const result = [];
      this.maxAskSize = 0;
      this.maxBidSize = 0;
      for (let i = 0; i < len; i++) {
        const _cellVariants = {};
        ['bid', 'ask'].forEach(type => {
          const order = orders[type + 's'][i];
          if (!order) return;
          const price = order[type];
          if (price && topPrices[type].length < 5 && !topPrices[type].includes(price)) topPrices[type].push(price);
          if (topPrices[type].includes(price)) _cellVariants[type] = topPrices[type].indexOf(price);
          this[`max${this._.capitalize(type)}Size`] = Math.max(
            this[`max${this._.capitalize(type)}Size`],
            order[type + '_size']
          );
        });

        result[i] = {
          _index: i,
          _cellVariants,
          ...orders.asks[i],
          ...orders.bids[i]
        };
      }
      return result;
    },
    fetch() {
      this.isLoading = true;
      this.isNoData = false;
      this.isError = false;
      this.streamingSubscribe().then(() => {
        //To handle cases where snapshots not received for un-permissioned symbols
        this.clearPendingTimeout();
        this.pendingTimeout = window.setTimeout(() => {
          this.isLoading = false;
          this.clearPendingTimeout();
        }, this.responseTimeout);
      });
    },
    clearPendingTimeout() {
      if (this.pendingTimeout) {
        window.clearTimeout(this.pendingTimeout);
        this.pendingTimeout = undefined;
      }
    },
    setRowsHeight() {
      this.gridOptions.api.setHeaderHeight(this.agGridRowHeight);
      this.gridOptions.api.resetRowHeights();
      // this.gridOptions.columnApi && this.gridOptions.columnApi.autoSizeAllColumns(true);
    },
    streamingConnect() {
      return new Promise((resolve, reject) => {
        if (API.Streaming.isConnected) resolve();
        else
          API.Streaming.connect({
            timeout: 10000,
            monitor: {
              interval: 10000,
              timeout: 5000
            },
            visibilityMonitorEnabled: true
          }).then(resolve, reject);
      });
    },
    streamingSubscribe() {
      return this.streamingConnect().then(() => {
        this.subscriptionId = API.Streaming.MarketDepth.subscribe(
          {
            rics: [this.mdRic]
          },
          // Subscribe Callback
          (_res, error) => {
            if (error) {
              console.warn(`Streaming subscription error for ${this.mdRic}: ${error}`);
              this.isLoading = false;
              this.isNoData = true;
            }
          },
          // Snapshot Callback
          this.handleSnapshot,
          // Update Callback
          this.handleUpdate
        );
      });
    },
    streamingUnsubscribe() {
      this.cachedData = {};
      this.gridOptions.api.setRowData([]); //discard old data
      if (this.subscriptionId) {
        API.Streaming.MarketDepth.unsubscribe(this.subscriptionId, (e, err) => {
          if (err) console.error('Not able to unsubscribe MarketDepth streaming', err);
        });
      }
    },
    handleSnapshot(response, error) {
      if (error) {
        console.error(error);
        if (this.isLoading) {
          this.isError = true;
          this.isLoading = false;
        } else {
          this.hasStreamingError = true;
        }
        return;
      }

      this.hasStreamingError = false;

      if (response.payload[0].ric !== this.mdRic) return;
      this.cachedData = response.payload[0].data;
      if (Object.keys(this.cachedData).length) {
        this.isNoData = false;
        this.throttledSetRowData();
        this.gridOptions.api.sizeColumnsToFit();
      } else {
        this.isNoData = true;
      }

      this.clearPendingTimeout();
      this.isLoading = false;
    },
    handleUpdate(response, err) {
      if (err) {
        this.hasStreamingError = true;
        return console.error(err);
      }
      this.hasStreamingError = false;
      // do not update if the data is coming from previous subscription with failed unsubscribe
      if (response.payload[0].ric !== this.mdRic) return;
      const payload = response.payload[0].data;
      Object.keys(payload).forEach(orderId => {
        if (!Object.values(payload[orderId]).length) {
          if (this.cachedData[orderId]) delete this.cachedData[orderId];
          return;
        }
        if (this.cachedData[orderId]) Object.assign(this.cachedData[orderId], payload[orderId]);
        else this.cachedData[orderId] = payload[orderId];
      });

      this.throttledSetRowData();
    },
    onCellKeyDown(params) {
      const event = params.event;
      const key = event.key;

      let target = event.target.querySelector('button');

      if (['Enter', 'Space'].includes(key) && params.column && ['ask', 'bid'].includes(params.column.colId) && target)
        target.click();
    },
    getContrastTextColor(hexcolor) {
      if (!hexcolor.startsWith('#')) return;
      return contrast(hexToRGB(hexcolor), [255, 255, 255]) > contrast(hexToRGB(hexcolor), [0, 0, 0])
        ? '#ffffff'
        : '#000000';
    }
  },
  created() {
    moment.locale(this.locale);
  },
  beforeDestroy() {
    this.streamingUnsubscribe();
    if (this.gridOptions) this.gridOptions = null;
    if (this.gridApi) this.gridApi = null;
  }
};
</script>

<style lang="scss">
@import '~@/styles/vendor/ag-grid';
</style>
